import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Icon } from '@iconify/react';
import { colors, font, radius } from '../../styles/tokens';

// One shared availability calendar for every ZONA surface that books ad days.
// The ads portal carries the original (TSX) — keep them in sync:
//   /Users/katherinesolano/Zona-ads-portal/src/components/AvailabilityCalendar/index.tsx
// This copy additionally takes `minDay` (see below), which the portal does
// not need — the portal only ever books forward from today.
//
// Selection is click-first-day / click-last-day INCLUSIVE, but the value
// reported up is [start, endExclusive) to match the server's live-serving
// window (an ad stops the moment date_end arrives), so the /ads payload
// stays a plain yyyy-MM-dd pair.

const fmtDay = (d) => {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const addDays = (str, n) => {
  const [y, m, d] = str.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  return fmtDay(dt);
};

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// `minDay` is the earliest selectable day, yyyy-MM-dd, defaulting to today.
// Editing an ad that is already running passes its existing start date so the
// run can be trimmed or extended without being forced to begin today.
const AvailabilityCalendar = ({ bookedDates, start, endExclusive, onChange, minDay }) => {
  const now = new Date();
  const today = fmtDay(now);
  const earliest = minDay && minDay < today ? minDay : today;

  const openOn = start || earliest;
  const [viewYear, setViewYear] = useState(() => Number(openOn.slice(0, 4)));
  const [viewMonth, setViewMonth] = useState(() => Number(openOn.slice(5, 7)) - 1);

  // The selected range can arrive after first paint (an edit form fetching its
  // ad), so follow it into view. Clicking a day never moves the view, since the
  // day clicked is already the month on screen.
  useEffect(() => {
    if (!start) return;
    setViewYear(Number(start.slice(0, 4)));
    setViewMonth(Number(start.slice(5, 7)) - 1);
  }, [start]);

  const booked = useMemo(() => new Set(bookedDates), [bookedDates]);

  const atEarliestMonth =
    viewYear === Number(earliest.slice(0, 4)) && viewMonth === Number(earliest.slice(5, 7)) - 1;

  const cells = useMemo(() => {
    const firstWeekday = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const out = Array(firstWeekday).fill(null);
    for (let d = 1; d <= daysInMonth; d++) {
      out.push(fmtDay(new Date(viewYear, viewMonth, d)));
    }
    return out;
  }, [viewYear, viewMonth]);

  const moveMonth = (delta) => {
    const next = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  };

  const handleClick = (day) => {
    if (day < earliest || booked.has(day)) return;
    // First click, restart click, or click before the current start → new start.
    if (!start || endExclusive || day < start) {
      onChange(day, '');
      return;
    }
    // Second click completes the range, last day inclusive.
    onChange(start, addDays(day, 1));
  };

  const lastSelected = endExclusive ? addDays(endExclusive, -1) : '';

  return (
    <Wrap>
      <Head>
        <NavButton type="button" onClick={() => moveMonth(-1)} disabled={atEarliestMonth} aria-label="Previous month">
          <Icon icon="solar:alt-arrow-left-outline" />
        </NavButton>
        <MonthLabel>{MONTHS[viewMonth]} {viewYear}</MonthLabel>
        <NavButton type="button" onClick={() => moveMonth(1)} aria-label="Next month">
          <Icon icon="solar:alt-arrow-right-outline" />
        </NavButton>
      </Head>

      <Grid>
        {WEEKDAYS.map((w, i) => (
          <Weekday key={`${w}-${i}`}>{w}</Weekday>
        ))}
        {cells.map((day, i) => {
          if (!day) return <div key={`pad-${i}`} />;
          const isPast = day < earliest;
          const isBooked = booked.has(day);
          const inRange = !!start && (
            endExclusive ? day >= start && day < endExclusive : day === start
          );
          return (
            <Day
              key={day}
              type="button"
              disabled={isPast || isBooked}
              $booked={isBooked && !isPast}
              $selected={inRange}
              $edge={day === start || day === lastSelected}
              $today={day === today}
              onClick={() => handleClick(day)}
            >
              {Number(day.slice(-2))}
            </Day>
          );
        })}
      </Grid>

      <Legend>
        <LegendItem><Dot /> Available</LegendItem>
        <LegendItem><Dot $booked /> Booked</LegendItem>
        <LegendItem><Dot $selected /> Your dates</LegendItem>
      </Legend>

      <Hint>
        {!start
          ? 'Tap your first day'
          : !endExclusive
          ? 'Tap your last day (or the same day for a single day)'
          : 'Tap any day to start over'}
      </Hint>
    </Wrap>
  );
};

export default AvailabilityCalendar;

const Wrap = styled.div`
  background: ${colors.bgInput};
  border: 1px solid ${colors.border};
  border-radius: ${radius.xl};
  padding: 16px;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const NavButton = styled.button`
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: ${radius.sm};
  border: 1px solid ${colors.border};
  background: ${colors.surface1};
  color: ${colors.text};
  font-size: 16px;
  cursor: pointer;
  transition: background 160ms, border-color 160ms;

  &:hover:not(:disabled) {
    background: ${colors.surface2};
    border-color: ${colors.borderStrong};
  }
  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`;

const MonthLabel = styled.div`
  font-size: ${font.base};
  font-weight: 700;
  color: ${colors.text};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
`;

const Weekday = styled.div`
  text-align: center;
  font-size: ${font.xs};
  font-weight: 700;
  letter-spacing: 0.08em;
  color: ${colors.textFaint};
  padding: 4px 0 8px;
`;

const Day = styled.button`
  position: relative;
  aspect-ratio: 1;
  border-radius: ${radius.md};
  border: 1px solid ${({ $today, $selected }) => ($selected ? 'transparent' : $today ? colors.borderFocus : 'transparent')};
  font-size: ${font.md};
  font-weight: ${({ $selected }) => ($selected ? 700 : 500)};
  color: ${({ $selected, $booked }) => ($selected ? '#fff' : $booked ? colors.textFaint : colors.textSecondary)};
  background: ${({ $selected, $edge, $booked }) =>
    $selected && $edge ? colors.gradient
    : $selected ? colors.primaryBgStrong
    : $booked ? colors.dangerBg
    : 'transparent'};
  text-decoration: ${({ $booked }) => ($booked ? 'line-through' : 'none')};
  cursor: pointer;
  transition: background 140ms, color 140ms;

  &:hover:not(:disabled) {
    background: ${({ $selected, $edge }) => ($selected && $edge ? colors.gradient : colors.surface2)};
    color: #fff;
  }
  &:disabled {
    cursor: default;
    opacity: ${({ $booked }) => ($booked ? 0.85 : 0.3)};
  }
`;

const Legend = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 14px;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: ${font.sm};
  color: ${colors.textMuted};
`;

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: ${({ $booked, $selected }) =>
    $selected ? colors.gradient : $booked ? colors.dangerBg : colors.surface3};
  border: 1px solid ${({ $booked }) => ($booked ? 'rgba(239, 68, 68, 0.45)' : colors.border)};
`;

const Hint = styled.p`
  margin-top: 10px;
  font-size: ${font.sm};
  color: ${colors.textMuted};
`;
